// src/page/SummaryPoint.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';

export default function SummaryPoint() {
    useAuthGuard();
    const navigate = useNavigate();

    // === flatpickr for 照会期間 ===
    const rangeRef = useRef(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
    const fmtYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const fmtDateTime = (iso) => {
        if (!iso) return '';
        const dt = new Date(iso);
        if (isNaN(dt)) return '';
        return `${dt.getFullYear()}/${pad(dt.getMonth() + 1)}/${pad(dt.getDate())} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    };
    const rangeText = fromDate && toDate ? `${fmt(fromDate)} ~ ${fmt(toDate)}` : '';

    useEffect(() => {
        if (!window.flatpickr) return;
        const ja = window.flatpickr.l10ns?.ja || {};
        const el = document.getElementById('summary_period_range');
        if (!el) return;

        const fp = window.flatpickr(el, {
            mode: 'range',
            dateFormat: 'Y/m/d',
            locale: { ...ja, rangeSeparator: ' ~ ' },
            onChange: (dates) => {
                if (dates?.length === 2) {
                    setFromDate(dates[0]);
                    setToDate(dates[1]);
                } else if (!dates?.length) {
                    setFromDate('');
                    setToDate('');
                }
            }
        });
        rangeRef.current = fp;
        return () => { try { fp.destroy(); } catch { } };
    }, []);

    // ===== filters (UI states) =====
    const [keyword, setKeyword] = useState('');
    // ポイント区分 0..8
    const [typeChecks, setTypeChecks] = useState({
        0: false, // 初期ポイント
        1: false, // 起動ポイント
        2: false, // 行動ポイント
        3: false, // クーポン利用
        4: false, // 交通ICカード連携
        5: false, // シェアサイクル連携
        6: false, // BPサービス連携
        7: false, // becoz連携ポイント
        8: false, // キャンペーン応募
    });
    // ステータス 0..1
    const [statusChecks, setStatusChecks] = useState({
        0: false, // 予約
        1: false, // 確定
    });

    // ===== committed query states (API uses these) =====
    const [qKeyword, setQKeyword] = useState('');
    const [qFrom, setQFrom] = useState(null); // Date|null
    const [qTo, setQTo] = useState(null);     // Date|null
    const [qTypes, setQTypes] = useState('');   // "0,1,2"
    const [qStatus, setQStatus] = useState(''); // "0,1"

    // ===== list data & pagination =====
    const [rows, setRows] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));
    const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const last = Math.min(total, page * pageSize);
    const goPrev = () => setPage(p => Math.max(1, p - 1));
    const goNext = () => setPage(p => Math.min(pageCount, p + 1));

    // condensed pager (same style as SummaryView)
    const buildPageItems = (current, totalPages, window = 5) => {
        if (totalPages <= 1) return [1];
        const items = [];
        const add = (v) => { if (items.length === 0 || items[items.length - 1] !== v) items.push(v); };
        const left = Math.max(1, current - window);
        const right = Math.min(totalPages, current + window);
        add(1);
        if (left > 2) add('…');
        for (let p = left; p <= right; p++) add(p);
        if (right < totalPages - 1) add('…');
        if (totalPages > 1) add(totalPages);
        return items;
    };
    const pageItems = useMemo(() => buildPageItems(page, pageCount, 5), [page, pageCount]);

    // ===== sorting =====
    // sort keys: user|type|status|amount|acted
    const [sort, setSort] = useState({ key: 'acted', dir: 'desc' });
    const toggleSort = (key) => setSort(prev => (
        prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    ));

    // ===== labels =====
    const statusLabel = (v) => ({ "0": "予約", "1": "確定" }[String(v ?? '')] ?? '');
    const typeLabel = (v) => ({
        "0": "初期ポイント",
        "1": "起動ポイント",
        "2": "行動ポイント",
        "3": "クーポン利用",
        "4": "交通ICカード連携",
        "5": "シェアサイクル連携",
        "6": "BPサービス連携",
        "7": "becoz連携ポイント",
        "8": "キャンペーン応募",
    }[String(v ?? '')] ?? '');

    // ===== fetch list =====
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setListLoading(true);
                setListError('');
                const token = localStorage.getItem('access_token') || '';
                const url = new URL(`${API_BASE_URL}/v1/summary/point`);
                url.searchParams.set('page', String(page));
                url.searchParams.set('size', String(pageSize));
                url.searchParams.set('sort', sort.key);
                url.searchParams.set('dir', sort.dir);
                if (qKeyword) url.searchParams.set('keyword', qKeyword);
                if (qFrom && qTo) { url.searchParams.set('from', fmtYMD(qFrom)); url.searchParams.set('to', fmtYMD(qTo)); }
                if (qTypes) url.searchParams.set('actionTypes', qTypes);
                if (qStatus) url.searchParams.set('status', qStatus);

                const res = await fetch(url.toString(), {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const j = await res.json();
                setRows(Array.isArray(j.items) ? j.items : []);
                setTotal(Number(j.total) || 0);
                if (j.size && Number(j.size) !== pageSize) setPageSize(Number(j.size));
            } catch (e) {
                if (e?.name === 'AbortError') return;
                console.error(e);
                setRows([]);
                setListError('読み込みに失敗しました');
            } finally {
                setListLoading(false);
            }
        })();
        return () => ac.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, sort, qKeyword, qFrom, qTo, qTypes, qStatus]);

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
        if (page < 1) setPage(1);
    }, [page, pageCount]);

    // ===== submit & clear =====
    const handleSubmit = (e) => {
        e.preventDefault();
        setQKeyword(keyword.trim());
        setQFrom(fromDate || null);
        setQTo(toDate || null);
        const selTypes = Object.entries(typeChecks).filter(([, v]) => v).map(([k]) => k).join(',');
        const selStatus = Object.entries(statusChecks).filter(([, v]) => v).map(([k]) => k).join(',');
        setQTypes(selTypes);
        setQStatus(selStatus);
        setPage(1);
    };

    const handleClear = () => {
        setKeyword('');
        setFromDate('');
        setToDate('');
        setTypeChecks({ 0: false, 1: false, 2: false, 3: false, 4: false, 5: false, 6: false, 7: false, 8: false });
        setStatusChecks({ 0: false, 1: false });
        setQKeyword('');
        setQFrom(null);
        setQTo(null);
        setQTypes('');
        setQStatus('');
        setPage(1);
    };

    // ===== CSV export =====
    const handleExportCsv = async () => {
        try {
            const token = localStorage.getItem('access_token') || '';
            const url = new URL(`${API_BASE_URL}/v1/summary/point/export`);
            url.searchParams.set('sort', sort.key);
            url.searchParams.set('dir', sort.dir);
            if (qKeyword) url.searchParams.set('keyword', qKeyword);
            if (qFrom && qTo) {
                const s = new Date(qFrom.getFullYear(), qFrom.getMonth(), qFrom.getDate(), 0, 0, 0, 0);
                const e = new Date(qTo.getFullYear(), qTo.getMonth(), qTo.getDate(), 23, 59, 59, 999);
                url.searchParams.set('from', s.toISOString()); // export expects RFC3339
                url.searchParams.set('to', e.toISOString());
            }
            if (qTypes) url.searchParams.set('actionTypes', qTypes);
            if (qStatus) url.searchParams.set('status', qStatus);

            const res = await fetch(url.toString(), {
                headers: { Accept: 'text/csv', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const d = new Date();
            const fname = `ポイント実績_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}.csv`;
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = fname;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (e) {
            console.error(e);
            alert('CSVのダウンロードに失敗しました');
        }
    };

    // ===== Render =====
    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/summary">実績管理</a></li>
                    <li className="breadcrumb-item active">ポイント実績</li>
                </ol>
            </nav>

            <div className="mb-9">
                <div className="row g-3 mb-4">
                    <div className="col-auto">
                        <h2 className="mb-0">ポイント実績</h2>
                    </div>
                </div>

                <div id="summary">
                    <div className="mb-4">
                        <form className="position-relative w-100" onSubmit={handleSubmit}>
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                <div className="search-box">
                                    <input
                                        aria-label="Search"
                                        className="form-control search-input search"
                                        placeholder="会員番号"
                                        type="search"
                                        value={keyword}
                                        onChange={(e) => setKeyword(e.target.value)}
                                    />
                                    <span className="fas fa-search search-box-icon"></span>
                                </div>
                                <div>
                                    <label className="form-label mb-1 d-block">ポイント区分</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={typeChecks[0]} onChange={(e) => setTypeChecks(s => ({ ...s, 0: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">初期ポイント</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={typeChecks[1]} onChange={(e) => setTypeChecks(s => ({ ...s, 1: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">起動ポイント</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={typeChecks[2]} onChange={(e) => setTypeChecks(s => ({ ...s, 2: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">行動ポイント</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={typeChecks[3]} onChange={(e) => setTypeChecks(s => ({ ...s, 3: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">クーポン利用</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={typeChecks[4]} onChange={(e) => setTypeChecks(s => ({ ...s, 4: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">交通ICカード連携</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={typeChecks[5]} onChange={(e) => setTypeChecks(s => ({ ...s, 5: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">シェアサイクル連携</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={typeChecks[6]} onChange={(e) => setTypeChecks(s => ({ ...s, 6: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">BPサービス連携</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={typeChecks[7]} onChange={(e) => setTypeChecks(s => ({ ...s, 7: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">becoz連携ポイント</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={typeChecks[8]} onChange={(e) => setTypeChecks(s => ({ ...s, 8: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">キャンペーン応募</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap gap-3 align-items-end mt-3">
                                <div className="search-box">
                                    <label className="form-label mb-1 d-block">照会期間</label>
                                    <div className="position-relative">
                                        <input
                                            id="summary_period_range"
                                            className="form-control search-input search pe-5"
                                            type="text"
                                            name=""
                                            placeholder="YYYY/MM/DD ~ YYYY/MM/DD"
                                            value={rangeText}
                                            readOnly
                                        />
                                        <span
                                            className="fas fa-calendar-alt search-box-icon position-absolute top-50 translate-middle-y"
                                            style={{ left: 12, pointerEvents: 'none' }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="form-label mb-1 d-block">ステータス</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={statusChecks[0]} onChange={(e) => setStatusChecks(s => ({ ...s, 0: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">予約</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox"
                                                checked={statusChecks[1]} onChange={(e) => setStatusChecks(s => ({ ...s, 1: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">確定</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex gap-2 mt-3">
                                <button type="submit" className="btn btn-primary">検索</button>
                                <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>クリア</button>

                                <div className="ms-xxl-auto d-flex gap-3">
                                    <button className="btn btn-link text-body me-4 px-0" type="button" onClick={handleExportCsv}>
                                        <span className="fa-solid fa-file-export fs-9 me-2"></span>CSVダウンロード
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                        <div className="table-responsive scrollbar mx-n1 px-1">
                            <table className="table fs-9 mb-0">
                                <thead>
                                    <tr>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('user'); setPage(1); }}>会員番号<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('type'); setPage(1); }}>ポイント区分<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th className="align-middle ps-3 text-nowrap" role="button">ポイント識別ID</th>
                                        <th className="align-middle ps-3 text-nowrap" role="button">操作摘要</th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('amount'); setPage(1); }}>ポイント数<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('status'); setPage(1); }}>ステータス<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('acted'); setPage(1); }}>取得/利用日時<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listError && (
                                        <tr><td className="ps-3 text-danger" colSpan={8}>{listError}</td></tr>
                                    )}
                                    {!listError && listLoading && (
                                        <tr><td className="ps-3 text-body-tertiary" colSpan={8}>読み込み中…</td></tr>
                                    )}
                                    {!listError && !listLoading && rows.length === 0 && (
                                        <tr><td className="ps-3 text-body-tertiary" colSpan={8}>データがありません</td></tr>
                                    )}
                                    {!listError && !listLoading && rows.map((r, i) => (
                                        <tr key={i}>
                                            <td className="ps-3">{r.userNumber || ''}</td>
                                            <td className="ps-3">{typeLabel(r.pointActionType)}</td>
                                            <td className="ps-3">{r.pointActionId || ''}</td>
                                            <td className="ps-3">{r.pointActionTypeDetail || ''}</td>
                                            <td className="ps-3">{r.amount ?? ''}</td>
                                            <td className="ps-3">{statusLabel(r.pointStatus)}</td>
                                            <td className="ps-3">{fmtDateTime(r.actedAt)}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => navigate(`/summary/point/${r.pointActionId}/edit`)}
                                                    type="button"
                                                >
                                                    修正
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="row align-items-center justify-content-between py-2 pe-0 fs-9">
                            <div className="col-auto d-flex align-items-center gap-3">
                                <p className="mb-0 d-none d-sm-block me-1 fw-semibold text-body">
                                    {total === 0 ? '0 items' : `${first} to ${last} items of ${total}`}
                                </p>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                                <button type="button" className="page-link" onClick={goPrev} disabled={page <= 1}>
                                    <span className="fas fa-chevron-left" />
                                </button>
                                <ul className="mb-0 pagination">
                                    {pageItems.map((it, idx) =>
                                        it === '…' ? (
                                            <li key={`ellipsis-${idx}`} className="page-item disabled">
                                                <span className="page-link">…</span>
                                            </li>
                                        ) : (
                                            <li key={it} className={`page-item ${it === page ? 'active' : ''}`}>
                                                <button type="button" className="page-link" onClick={() => setPage(it)}>
                                                    {it}
                                                </button>
                                            </li>
                                        )
                                    )}
                                </ul>
                                <button type="button" className="page-link pe-0" onClick={goNext} disabled={page >= pageCount}>
                                    <span className="fas fa-chevron-right" />
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        </Layout>
    );
}
